import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { Switch } from "@/components/ui/switch";
import { useOrganizationSettingData } from "@/data/use-organization-settings-data";
import { useOrganizationData } from "@/data/use-organizations-data";
import { useParams } from "@tanstack/react-router";
import { toast } from "sonner";

const flagStatus = (value: string | null) => {
  if (value === "enabled") return "Enabled";
  if (value === "disabled") return "Disabled";
  return "Not set";
};

export default function GlobalAdminOrganizationFeatureFlagsPage() {
  const { organizationUuid } = useParams({ strict: false }) as {
    organizationUuid?: string;
  };
  const { organization } = useOrganizationData(organizationUuid);

  const {
    value: multipleExpertsFeatureFlag,
    setValue: setMultipleExpertsFeatureFlag,
    isLoading,
  } = useOrganizationSettingData(
    organizationUuid,
    "FEATURE_FLAG_MULTIPLE_EXPERTS_CHAT",
  );
  const { value: researchFeatureFlag, setValue: setResearchFeatureFlag } =
    useOrganizationSettingData(organizationUuid, "FEATURE_FLAG_RESEARCHER");
  const {
    value: advancedModelsFeatureFlag,
    setValue: setAdvancedModelsFeatureFlag,
  } = useOrganizationSettingData(
    organizationUuid,
    "FEATURE_FLAG_ADVANCED_MODELS",
  );
  const {
    value: toolAutoApproveFeatureFlag,
    setValue: setToolAutoApproveFeatureFlag,
  } = useOrganizationSettingData(
    organizationUuid,
    "FEATURE_FLAG_TOOL_AUTO_APPROVE",
  );
  const {
    value: sharePointAutoSyncFeatureFlag,
    setValue: setSharePointAutoSyncFeatureFlag,
  } = useOrganizationSettingData(
    organizationUuid,
    "FEATURE_FLAG_SHAREPOINT_AUTO_SYNC",
  );
  const {
    value: salthubAttachFeatureFlag,
    setValue: setSalthubAttachFeatureFlag,
  } = useOrganizationSettingData(
    organizationUuid,
    "FEATURE_FLAG_SALTHUB_ATTACH",
  );

  const handleToggle = async (
    current: string | null,
    setValue: (value: "enabled" | "disabled") => Promise<void>,
    label: string,
  ) => {
    try {
      await setValue(current === "enabled" ? "disabled" : "enabled");
      toast.success(`Organization default updated: ${label}.`);
    } catch {
      toast.error(`Failed to update organization default: ${label}.`);
    }
  };

  if (!organization) return null;

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Feature Flags</PageHeader.Title>
      </PageHeader>
      <PageBody innerClassName="gap-8">
        <p className="text-muted-foreground text-sm">
          These switches write the organization default. A workspace can still
          set its own value. When this organization has no row, workspaces use
          the system default.
        </p>
        <div className="flex flex-col gap-4">
          <FlagSwitch
            id="org-multiple-experts-chat-feature-flag"
            label="Multiple Experts Chat"
            status={flagStatus(multipleExpertsFeatureFlag)}
            checked={multipleExpertsFeatureFlag === "enabled"}
            disabled={isLoading}
            onCheckedChange={() =>
              handleToggle(
                multipleExpertsFeatureFlag,
                setMultipleExpertsFeatureFlag,
                "Multiple Experts Chat",
              )
            }
          />
          <FlagSwitch
            id="org-research-feature-flag"
            label="Research Feature"
            status={flagStatus(researchFeatureFlag)}
            checked={researchFeatureFlag === "enabled"}
            disabled={isLoading}
            onCheckedChange={() =>
              handleToggle(
                researchFeatureFlag,
                setResearchFeatureFlag,
                "Research Feature",
              )
            }
          />
          <FlagSwitch
            id="org-advanced-models-feature-flag"
            label="Advanced Models (e.g. Claude Opus)"
            status={flagStatus(advancedModelsFeatureFlag)}
            checked={advancedModelsFeatureFlag === "enabled"}
            disabled={isLoading}
            onCheckedChange={() =>
              handleToggle(
                advancedModelsFeatureFlag,
                setAdvancedModelsFeatureFlag,
                "Advanced Models",
              )
            }
          />
          <FlagSwitch
            id="org-tool-auto-approve-feature-flag"
            label="Tool Auto-Approve (skip HITL for activate skill)"
            status={flagStatus(toolAutoApproveFeatureFlag)}
            checked={toolAutoApproveFeatureFlag === "enabled"}
            disabled={isLoading}
            onCheckedChange={() =>
              handleToggle(
                toolAutoApproveFeatureFlag,
                setToolAutoApproveFeatureFlag,
                "Tool Auto-Approve",
              )
            }
          />
          <FlagSwitch
            id="org-sharepoint-auto-sync-feature-flag"
            label="SharePoint Auto-Sync (Expert knowledge)"
            status={flagStatus(sharePointAutoSyncFeatureFlag)}
            checked={sharePointAutoSyncFeatureFlag === "enabled"}
            disabled={isLoading}
            onCheckedChange={() =>
              handleToggle(
                sharePointAutoSyncFeatureFlag,
                setSharePointAutoSyncFeatureFlag,
                "SharePoint Auto-Sync",
              )
            }
          />
          <FlagSwitch
            id="org-salthub-attach-feature-flag"
            label="SaltHub Attach (add new users by SaltHub client ID)"
            status={flagStatus(salthubAttachFeatureFlag)}
            checked={salthubAttachFeatureFlag === "enabled"}
            disabled={isLoading}
            onCheckedChange={() =>
              handleToggle(
                salthubAttachFeatureFlag,
                setSalthubAttachFeatureFlag,
                "SaltHub Attach",
              )
            }
          />
        </div>
      </PageBody>
    </Page>
  );
}

const FlagSwitch = ({
  id,
  label,
  status,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string;
  label: string;
  status: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: () => void;
}) => (
  <div className="flex items-center gap-2">
    <Switch
      id={id}
      onCheckedChange={onCheckedChange}
      checked={checked}
      disabled={disabled}
    />
    <label htmlFor={id} className="select-none">
      {label}
    </label>
    <span className="text-muted-foreground text-sm">{status}</span>
  </div>
);
